import { targetsWithoutAll } from "@/shared/utils/constant";
import { useState } from "react";
import { isGreaterThanZero } from "@/shared/utils/helpers";
import Select from "../Select/Select";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Product, Topping } from "@/shared/utils/interfaces";
import useVatHook from "@/shared/hooks/useVatHook.js";
import TargetOverrideList from "../TargetOverrides/TargetOverrideList";

interface VatPerTargetProps {
  data: Partial<Product | Topping>;
}

const VatPerTarget = ({ data }: VatPerTargetProps) => {
  const { t } = useTranslation();
  const formContext = useFormContext();
  const { vat_enum_per_target } = data ?? {};
  const { control, errors } = formContext;
  const defaultTargetIds = Object.keys(vat_enum_per_target ?? {});
  const { vats } = useVatHook();

  const [localTargets, setLocalTargets] = useState(
    isGreaterThanZero(defaultTargetIds)
      ? targetsWithoutAll
          .filter((t) => defaultTargetIds.includes(`${t.value}`))
          .map((t) => ({
            ...t,
            vat_enum_per_target: Number(vat_enum_per_target[t.value]),
          }))
      : []
  );

  return (
    <TargetOverrideList
      icon="percent"
      title={t("menu.vatPerTarget")}
      description={t("targetOverrides.vatHint")}
      targets={localTargets}
      onAdd={(target) =>
        setLocalTargets((prev) => [
          ...prev,
          { ...target, vat_enum_per_target: undefined },
        ])
      }
      onRemove={(target) =>
        setLocalTargets((prev) => prev.filter((t) => t.value !== target.value))
      }
      renderControl={(target) => (
        <Controller
          name={`vat_enum_per_target.#${target.value}`}
          control={control}
          rules={{
            required: { value: true, message: t("fieldRequired") },
          }}
          defaultValue={target?.vat_enum_per_target}
          render={({ onChange, value }) => (
            <Select
              options={vats}
              defaultValue={vats.find((v) => v.id === value)}
              onChange={(vat) => onChange(vat.id)}
              placeholder={t("vat")}
              getOptionLabel={(option) => t(`${option.label}`)}
              getOptionValue={(option) => option.id}
              error={errors?.vat_enum_per_target?.[`#${target.value}`]?.message}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999999 }),
              }}
              menuPortalTarget={document.getElementById("root")}
            />
          )}
        />
      )}
    />
  );
};

export default VatPerTarget;
